import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, GameData, REGIONS, SHAPES } from '../gameData';
import SoundAssetKeys from "../SoundAssetKeys";

export default class SeagullArea extends RegionArea
{
    /**
     * Create the seagull area.
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[REGIONS.BEACH].SEAGULL);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.leftFoot.setAlpha(0);
        this.rightFoot.setAlpha(0);
        this.beak.setAlpha(0);
        this.head.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.leftFoot.disableInteractive();
        this.rightFoot.disableInteractive();
        this.beak.disableInteractive();
        this.head.disableInteractive();
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.leftFoot.setAlpha(0.000001);
        this.rightFoot.setAlpha(0.000001);
        this.beak.setAlpha(0.000001);
        this.head.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        let footHitArea = new Phaser.Geom.Polygon(
            [
                { x: 0, y: 31 },
                { x: 69, y: 1 },
                { x: 59, y: 62 },
            ]);

        this.leftFoot = ABCya.Images.addImage(620, 513, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Slct_Ft, this.parentRegion).setInteractive(footHitArea, Phaser.Geom.Polygon.Contains);
        this.leftFoot.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
        {
            this._onOverlayPointerDown(this.leftFoot, SHAPES.TRIANGLE);
        });
        this.rightFoot = ABCya.Images.addImage(693, 550, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Slct_Ft, this.parentRegion).setInteractive(footHitArea, Phaser.Geom.Polygon.Contains);
        this.rightFoot.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.rightFoot, SHAPES.TRIANGLE);
        });

        let beakHitArea = new Phaser.Geom.Polygon(
            [
                { x: 1, y: 81 },
                { x: 27, y: 1 },
                { x: 72, y: 41 },
            ]);
        this.beak = ABCya.Images.addImage(570, 352, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Slct_Beak, this.parentRegion).setInteractive(beakHitArea, Phaser.Geom.Polygon.Contains);
        this.beak.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.beak, SHAPES.CONE);
        });

        this.head = ABCya.Images.addImage(600, 306, UIAssetKeys._05_Beach_Zoom_In_Seagull_Seagl_Slct_Hd, this.parentRegion);
        this.head.setInteractive(new Phaser.Geom.Circle(this.head.width / 2, this.head.height / 2, this.head.width / 2), Phaser.Geom.Circle.Contains);
        this.head.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this._onOverlayPointerDown(this.head, SHAPES.SPHERE);
        });

        this.shapeOverlays[SHAPES.TRIANGLE] = [this.rightFoot, this.leftFoot];
        this.shapeOverlays[SHAPES.CONE] = [this.beak];
        this.shapeOverlays[SHAPES.SPHERE] = [this.head];

        this._createAreaInformation(0x5f5fd6, 0x5f5fd6);
    }

    /**
     * Callback function when an overlay is clicked on. Extends the original function and hides the area instructions.
     * @param {Phaser.GameObjects.Image} overlay the shape overlay that was clicked on
     * @param {string} shape the SHAPE enum value that the overlay belongs to
     */
    _onOverlayPointerDown(overlay, shape)
    {
        if (this.parentRegion.areaInstructionsPopup.state == 1)
        {
            this.parentRegion.areaInstructionsPopup.state = 2;
            ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup, 350, { y: this.parentRegion.areaInstructionsPopup.offScreenPosition.y }, Phaser.Math.Easing.Back.In);
        }

        super._onOverlayPointerDown(overlay, shape);
    }

    /**
     * Creates the shape information for the area. These are the buttons that can be clicked to find the shapes.
     * Extends the original function and adds an additional listener to show the 2nd set of instructions when a shape name is clicked.
     * @param {number} numberFontColor the number font color
     * @param {number} textFontColor the text font color
     */
    _createAreaInformation(numberFontColor, textFontColor)
    {
        super._createAreaInformation(numberFontColor, textFontColor);

        for (let shape in GameData.region_data[this.region][this.area])
        {
            let containerShapeObj = this.areaInformation[shape];
            containerShapeObj.shapeButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
            {
                if (this.parentRegion.areaInstructionsPopup.state == 0)
                {
                    this.parentRegion.areaInstructionsPopup.state = 1;
                    if (this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.isPlaying)
                    {
                        this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.stop();
                    }
                    ABCya.Audio.playOneShot(SoundAssetKeys._locate_that_shape, this.parentRegion.scene, 1, false, 0.25);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.firstInstructions, 350, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.secondInstructions, 350, { alpha: 1, delay: 300 }, Phaser.Math.Easing.Quadratic.Out);
                }
            });
        }
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.sandcastle, this.parentRegion.lifeguardContainer, this.parentRegion.beachball
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.seagullContainer, duration: 1000, props: { scale: 1, x: 199, y: 200 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 }
        );

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.leftFoot.disableInteractive();
        this.rightFoot.disableInteractive();
        this.beak.disableInteractive();
        this.head.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.seagullContainer, duration: 1000, props: { scale: 0.12, x: this.parentRegion.seagullContainer.onScreenPosition.x, y: this.parentRegion.seagullContainer.onScreenPosition.y }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.lifeguardContainer, this.parentRegion.beachball, this.parentRegion.sandcastle], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
        );

        return tweens;
    }
}